// Unified client-side file parsing.
// csv / tsv / txt  -> Papa Parse (delimiter auto-detected)
// xls / xlsx       -> read-excel-file (first sheet)
// Everything runs in the browser; no bytes leave the page.

import Papa from 'papaparse';
import { isOrdinaryHeader } from './safeMapping';
import { readSheet } from 'read-excel-file/browser';
import type { ParsedFile, ParsedUpload } from './types';

export const ACCEPTED_EXTENSIONS = ['.csv', '.tsv', '.txt', '.xls', '.xlsx'];

/** Hard cap on imported data rows (mirrors the backend MAX_ROWS guard). Keep the two in lockstep:
 * stopping here gives the user an immediate, localised error instead of a server-side rejection
 * after the whole file has been parsed and mapped. */
export const MAX_ROWS = 1000;

/** Thrown when an uploaded file has no detectable header row. The message is a translation key
 * (under the `inventory-import` namespace) so the UI can localise it. */
export const NO_HEADERS_ERROR_KEY = 'upload.error_no_columns';

/** Thrown when the file's extension isn't one we can parse. */
export const UNSUPPORTED_EXTENSION_ERROR_KEY = 'upload.error_unsupported_type';

/** Thrown when the file has more data rows than MAX_ROWS. */
export const TOO_MANY_ROWS_ERROR_KEY = 'upload.error_too_many_rows';

/** Thrown when a delimited file is malformed (e.g. unterminated quotes) and can't be parsed. */
export const PARSE_ERROR_KEY = 'upload.error_parse';

const SPREADSHEET = /\.(xlsx|xls)$/i;

/** Whether a file name ends in one of the extensions we can parse (case-insensitive). */
export function isAcceptedFile(name: string): boolean {
  const lower = name.toLowerCase();
  return ACCEPTED_EXTENSIONS.some((ext) => lower.endsWith(ext));
}

function normalizeRows(
  rawHeaders: string[],
  rows: Record<string, unknown>[]
): ParsedFile['rows'] {
  return rows.map((row) => {
    const out: Record<string, string> = Object.create(null);
    for (const header of rawHeaders) {
      const value = row[header];
      out[header] =
        value === null || value === undefined
          ? ''
          : // read-excel-file returns a real Date for date-formatted cells; show a tidy ISO date
            // instead of the verbose "Mon Jan 01 2024 …" toString (no field is date-typed, so the
            // row is still flagged, but the displayed value/message is readable).
            value instanceof Date
            ? value.toISOString().slice(0, 10)
            : String(value).trim();
    }
    return out;
  });
}

/** True when every cell of a normalized row is blank (used to drop xlsx interior blank rows). */
function isBlankRow(row: Record<string, string>): boolean {
  return Object.values(row).every((v) => v === '');
}

async function parseDelimited(file: File): Promise<ParsedUpload> {
  const text = await file.text();
  const result = Papa.parse<Record<string, string>>(text, {
    header: true,
    skipEmptyLines: 'greedy',
    transformHeader: (h: string) => h.trim(),
  });
  // Papa reports a few benign conditions as errors: FieldMismatch (a ragged row — normalizeRows
  // fills the missing cells) and an UndetectableDelimiter 'Delimiter' warning (single-column files,
  // where it correctly defaults to comma). Only a 'Quotes' error means genuinely malformed data
  // (e.g. an unterminated quote) that would import silently-garbled, so fail on that.
  if (result.errors.some((e: { type: string }) => e.type === 'Quotes')) {
    throw new Error(PARSE_ERROR_KEY);
  }
  const headers = (result.meta.fields ?? []).filter(
    (h: string) => h.length > 0 && isOrdinaryHeader(h)
  );
  return {
    fileName: file.name,
    headers,
    rows: normalizeRows(headers, result.data),
    rowNumbers: delimitedRowNumbers(text, result.meta.delimiter),
    renamedHeaders: renamedHeadersOf(headers, result.meta.renamedHeaders ?? {}),
  };
}

// Papa's skipEmptyLines leaves no trace of the rows it dropped, so re-read the records unskipped
// and number the ones its 'greedy' rule keeps; the first of those is the header. Papa trims and
// de-duplicates the first record's cells before that rule runs, so a first record of several
// empty cells becomes "", "_1", … and is kept as the header.
function delimitedRowNumbers(text: string, delimiter: string): number[] {
  const records = Papa.parse<string[]>(text, { delimiter }).data;
  return records
    .map((record, index) => ({
      blank:
        index === 0
          ? record.length === 1 && record[0].trim() === ''
          : record.join('').trim() === '',
      rowNumber: index + 1,
    }))
    .filter((record) => !record.blank)
    .slice(1)
    .map((record) => record.rowNumber);
}

function renamedHeadersOf(
  headers: string[],
  renamed: Record<string, string>
): Record<string, string> {
  return Object.assign(
    Object.create(null) as Record<string, string>,
    Object.fromEntries(
      Object.entries(renamed).filter(([header]) => headers.includes(header))
    )
  );
}

function cellToString(cell: unknown): string {
  return cell === null || cell === undefined ? '' : String(cell);
}

async function parseSpreadsheet(file: File): Promise<ParsedUpload> {
  // readSheet returns the first sheet as rows of raw cell values; row 0 is the header row.
  const grid = await readSheet(file);
  if (grid.length === 0) {
    return {
      fileName: file.name,
      headers: [],
      rows: [],
      rowNumbers: [],
      renamedHeaders: {},
    };
  }
  // Per-column key (blank for unnamed columns). De-dupe repeated header names the way Papa Parse
  // does for CSVs ("Length", "Length_1", …) so two same-named columns don't collapse into one.
  const seen = new Map<string, number>();
  const renamed: Record<string, string> = {};
  const keyAt = grid[0].map((cell: unknown) => {
    const name = cellToString(cell).trim();
    if (!name) return '';
    const n = seen.get(name) ?? 0;
    seen.set(name, n + 1);
    if (n === 0) return name;
    renamed[`${name}_${n}`] = name;
    return `${name}_${n}`;
  });
  const headers = keyAt.filter(
    (key: string) => key.length > 0 && isOrdinaryHeader(key)
  );
  const rows = grid.slice(1).map((row: unknown[]) => {
    const out: Record<string, unknown> = Object.create(null);
    keyAt.forEach((key: string, i: number) => {
      if (key && isOrdinaryHeader(key)) out[key] = row[i];
    });
    return out;
  });
  // read-excel-file only trims trailing blank rows, so a sectioned sheet keeps interior blank rows.
  // Drop them here so the xlsx path matches Papa's skipEmptyLines:'greedy' (and they don't count
  // toward MAX_ROWS or surface as phantom required-field errors).
  const numbered = normalizeRows(headers, rows)
    .map((row, index) => ({ row, rowNumber: index + 2 }))
    .filter(({ row }) => !isBlankRow(row));
  return {
    fileName: file.name,
    headers,
    rows: numbered.map(({ row }) => row),
    rowNumbers: numbered.map(({ rowNumber }) => rowNumber),
    renamedHeaders: renamedHeadersOf(headers, renamed),
  };
}

export async function parseFile(file: File): Promise<ParsedUpload> {
  // Reject by extension up front so both the drop and the file-picker paths get the same message,
  // and we never hand an unparseable file to Papa Parse / read-excel-file.
  if (!isAcceptedFile(file.name)) {
    throw new Error(UNSUPPORTED_EXTENSION_ERROR_KEY);
  }
  const parsed = SPREADSHEET.test(file.name)
    ? await parseSpreadsheet(file)
    : await parseDelimited(file);
  if (parsed.headers.length === 0) {
    throw new Error(NO_HEADERS_ERROR_KEY);
  }
  if (parsed.rows.length > MAX_ROWS) {
    throw new Error(TOO_MANY_ROWS_ERROR_KEY);
  }
  return parsed;
}
